import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, CircularProgress, Paper, Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { ProcessorType } from "utils/utils";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import c from "lib/style/colorVar.scss";

// Mapping helpers to avoid duplication across default mappers
const PROCESSOR_TYPE_BY_KEY = {
  ALLINPAY: ProcessorType.ALLINPAY,
  FUBON: ProcessorType.FUBON,
  YEDPAY: ProcessorType.YEDPAY,
  AMEX_BBMSL: ProcessorType.AMEX_BBMSL,
  AMEX: ProcessorType.AMEX,
  HSBC: ProcessorType.HSBC,
  OCTOPUS_HKBN: ProcessorType.OCTOPUS_HKBN,
  OCTOPUS_BBMSL: ProcessorType.OCTOPUS_BBMSL,
};

const CARD_TYPES_PROCESSORS_MAPPER = {
  VISA: ["ALLINPAY", "FUBON", "YEDPAY"],
  MASTER: ["ALLINPAY", "FUBON", "YEDPAY"],
  UNIONPAY: ["ALLINPAY", "FUBON"],
  JCB: ["ALLINPAY", "YEDPAY"],
  AMEX: ["AMEX_BBMSL", "AMEX"],
  PAYME: ["HSBC"],
  OCTOPUS: ["OCTOPUS_HKBN", "OCTOPUS_BBMSL"],
};

const buildMapper = (cardTypesProcessorsMapper, buildExtra) => {
  const result = {};
  Object.entries(cardTypesProcessorsMapper).forEach(
    ([cardType, processorList]) => {
      result[cardType] = processorList.reduce((acc, processor) => {
        acc[processor] = {
          paymentGateway: PROCESSOR_TYPE_BY_KEY[processor],
          ...buildExtra(cardType, processor),
        };
        return acc;
      }, {});
    },
  );
  return result;
};

const CREATE_TYPE_RULES = {
  VISA: {
    ALLINPAY: { tidType: "outlinedText", midType: "" },
    FUBON: { tidType: "autoGenerate", midType: "autoGenerate" },
    YEDPAY: { tidType: "autoGenerate", midType: "" },
  },
  MASTER: {
    ALLINPAY: { tidType: "outlinedText", midType: "" },
    FUBON: { tidType: "autoGenerate", midType: "autoGenerate" },
    YEDPAY: { tidType: "autoGenerate", midType: "" },
  },
  UNIONPAY: {
    ALLINPAY: { tidType: "outlinedText", midType: "" },
    FUBON: { tidType: "autoGenerate", midType: "autoGenerate" },
  },
  JCB: {
    ALLINPAY: { tidType: "outlinedText", midType: "" },
    YEDPAY: { tidType: "autoGenerate", midType: "" },
  },
  AMEX: {
    AMEX_BBMSL: { tidType: "autoGenerate", midType: "autoGenerate" },
    AMEX: { tidType: "outlinedText", midType: "" },
  },
  PAYME: {
    HSBC: { tidType: "autoGenerate", midType: "" },
  },
  OCTOPUS: {
    OCTOPUS_HKBN: { tidType: "", midType: "" },
    OCTOPUS_BBMSL: { tidType: "", midType: "" },
  },
};

const defaultProcessorAccMapper = buildMapper(
  CARD_TYPES_PROCESSORS_MAPPER,
  () => ({
    tid: "",
    mid: "",
  }),
);

const defaultIsCreateProcessorAccMapper = buildMapper(
  CARD_TYPES_PROCESSORS_MAPPER,
  (card, gw) => {
    const { tidType = "", midType = "" } =
      (CREATE_TYPE_RULES[card] || {})[gw] || {};
    return { tidType, midType, tid: "", mid: "" };
  },
);

// DBS / DSB / GLOBALPAYMENTS
const defaultOtherProcessorAccMapper = {
  VISA: {},
  MASTER: {},
  UNIONPAY: {},
  JCB: {},
  AMEX: {
    AMEX_BBMSL: { paymentGateway: ProcessorType.AMEX_BBMSL, tid: "", mid: "" },
    AMEX: { paymentGateway: ProcessorType.AMEX, tid: "", mid: "" },
  },
  PAYME: {
    HSBC: { paymentGateway: ProcessorType.HSBC, tid: "", mid: "" },
  },
  OCTOPUS: {
    OCTOPUS_HKBN: {
      paymentGateway: ProcessorType.OCTOPUS_HKBN,
      tid: "",
      mid: "",
    },
    OCTOPUS_BBMSL: {
      paymentGateway: ProcessorType.OCTOPUS_BBMSL,
      tid: "",
      mid: "",
    },
  },
};

const EmployeeProcessorAccInfo = ({
  employee,
  isLoading,
  isDisabled,
  onEditRow,
  isCreate,
  processorAccMapper,
  setProcessorAccMapper,
}) => {
  const disabled = isLoading || isDisabled;
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    title: {
      fontWeight: 600,
      color: "rgba(0, 0, 0, 0.87)",
    },
    paper: {
      border: "1px solid #E0E0E0",
      borderRadius: "8px",
      overflowX: "auto",
    },
    table: {
      tableLayout: "fixed",
    },
    tableHead: {
      backgroundColor: "#28868e",
      "& th": {
        color: "#ffffff",
        fontWeight: 600,
        fontSize: "0.875rem",
        borderBottom: "1px solid #28868e",
        borderRight: "1px solid rgba(255, 255, 255, 0.28)",
        padding: "12px 16px",
      },
      "& th:last-child": {
        borderRight: "none",
      },
    },
    tableBody: {
      "& td": {
        borderRight: "1px solid #E0E0E0",
      },
      "& tr td:last-child:not(:only-child)": {
        borderRight: "none",
      },
    },
    tableRow: {
      "&:last-child td": { borderBottom: 0 },
      "& td": {
        padding: "8px 16px",
        borderBottom: "1px solid #F0F0F0",
        color: "inherit",
        verticalAlign: "middle",
      },
      height: "48px",
    },
    groupStartRow: {
      "& td": {
        borderTop: "1px solid #E0E0E0",
      },
      "&:first-of-type td": {
        borderTop: "none",
      },
    },
    groupCell: {
      verticalAlign: "top",
      fontWeight: 500,
    },
    loadingCell: {
      padding: 3,
      textAlign: "center",
    },
    tidCell: {
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    actionButtons: {
      display: "flex",
      gap: 1,
    },
    columnWidths: {
      cardType: { width: "15%" },
      paymentGateway: { width: "20%" },
      mid: { width: "20%" },
      tid: { width: "25%" },
      action: { width: "20%" },
    },
    outlinedText: {
      width: "100%",
      minWidth: "100px",
      boxSizing: "border-box",
    },
    outlinedTextContainer: {
      width: "100%",
    },
    autoGenerateCell: {
      color: "rgb(171, 171, 171)",
    },
  };

  // Use props if provided, otherwise fall back to local state
  const [localProcessorAccMapper, setLocalProcessorAccMapper] = useState(
    defaultProcessorAccMapper,
  );

  // Use the passed props or local state
  const currentProcessorAccMapper =
    processorAccMapper || localProcessorAccMapper;
  const currentSetProcessorAccMapper =
    setProcessorAccMapper || setLocalProcessorAccMapper;
  const [editingRows, setEditingRows] = useState({});
  const [editingOriginalValues, setEditingOriginalValues] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const { t } = useTranslation();
  const PAGE_LOCALE_KEY = "page.employees.";
  const EMPLOYEE_LOCALE_KEY = "page.employees.";

  useEffect(() => {
    const preference = JSON.parse(employee?.preference ?? "{}");
    const gatewayMap = preference?.gatewayMap ?? {};
    const isOtherProcessor = Object.values(gatewayMap).some((processor) =>
      ["DBS", "DSB", "GLOBALPAYMENTS"].includes(processor),
    );

    const updatedMapper = JSON.parse(
      JSON.stringify(
        isOtherProcessor
          ? defaultOtherProcessorAccMapper
          : isCreate
            ? defaultIsCreateProcessorAccMapper
            : defaultProcessorAccMapper,
      ),
    );

    if (isOtherProcessor) {
      const processor = Object.values(gatewayMap).find((p) => p !== "NONE");
      const { tid, mid } = employee ?? {};

      if (processor && tid && mid) {
        ["VISA", "MASTER", "JCB", "UNIONPAY"].forEach((cardType) => {
          updatedMapper[cardType] = {
            ...updatedMapper[cardType],
            ...(cardType === "UNIONPAY"
              ? {
                  [processor]: {
                    paymentGateway: processor,
                  },
                }
              : {
                  [processor]: {
                    ...updatedMapper[cardType]?.[processor],
                    tid,
                    mid,
                    paymentGateway: processor,
                  },
                }),
          };
        });
      }
    } else {
      // For create mode, populate employee data into empty string fields
      if (isCreate && employee?.tidmidList) {
        employee.tidmidList.forEach(
          ({ cardType, paymentGateway, tid = "", mid = "" }) => {
            const upperCardType = cardType?.toUpperCase();
            const upperGateway = paymentGateway?.toUpperCase();

            if (
              upperCardType &&
              upperGateway &&
              updatedMapper[upperCardType]?.[upperGateway]
            ) {
              // Only update if the current value is empty string (not "outlinedText" or "autoGenerate")
              if (updatedMapper[upperCardType][upperGateway].tid === "") {
                updatedMapper[upperCardType][upperGateway].tid = tid;
              }
              if (updatedMapper[upperCardType][upperGateway].mid === "") {
                updatedMapper[upperCardType][upperGateway].mid = mid;
              }
            }
          },
        );
      } else if (!isCreate) {
        // Original logic for edit mode
        employee?.tidmidList?.forEach(
          ({ cardType, paymentGateway, tid = "", mid = "" }) => {
            const upperCardType = cardType?.toUpperCase();
            const upperGateway = paymentGateway?.toUpperCase();

            if (
              upperCardType &&
              upperGateway &&
              updatedMapper[upperCardType]?.[upperGateway]
            ) {
              updatedMapper[upperCardType][upperGateway] = {
                ...updatedMapper[upperCardType][upperGateway],
                tid,
                mid,
              };
            }
          },
        );
      }
    }
    currentSetProcessorAccMapper(updatedMapper);
    setEditingRows({});
    setEditingOriginalValues({});
    setSavingRows({});
  }, [employee, isCreate]);

  // Generate table rows from processorAccMapper
  const generateTableRows = () => {
    const tableRows = [];

    Object.entries(currentProcessorAccMapper).forEach(
      ([cardType, gateways]) => {
        Object.entries(gateways).forEach(([gatewayKey, data]) => {
          tableRows.push({
            id: `${cardType}-${gatewayKey}`,
            cardType,
            paymentGateway: data.paymentGateway,
            tid: data.tid,
            mid: data.mid,
            tidType: data.tidType,
            midType: data.midType,
          });
        });
      },
    );

    return tableRows;
  };

  const tableRows = generateTableRows();
  const groupedTableRows = Object.entries(
    tableRows.reduce((groups, row) => {
      const paymentGatewayRows = groups[row.paymentGateway] || [];
      paymentGatewayRows.push(row);
      groups[row.paymentGateway] = paymentGatewayRows;
      return groups;
    }, {}),
  ).map(([paymentGateway, rows]) => ({
    id: paymentGateway,
    paymentGateway,
    rows,
    mid: rows[0]?.mid,
    tid: rows[0]?.tid,
    midType: rows[0]?.midType,
    tidType: rows[0]?.tidType,
  }));

  const hasProcessorMid = (processorRow) =>
    Boolean(processorRow?.mid && processorRow.mid !== "-");

  const updateProcessorFieldState = (field, value, processorRow) => {
    currentSetProcessorAccMapper((prevMapper) => {
      const updatedMapper = JSON.parse(JSON.stringify(prevMapper));

      Object.keys(updatedMapper).forEach((cardType) => {
        Object.keys(updatedMapper[cardType]).forEach((gatewayKey) => {
          if (
            updatedMapper[cardType][gatewayKey].paymentGateway ===
            processorRow.paymentGateway
          ) {
            updatedMapper[cardType][gatewayKey][field] = value;
          }
        });
      });

      return updatedMapper;
    });
  };

  const handleSave = async (row) => {
    setSavingRows((prevRows) => ({
      ...prevRows,
      [row.id]: true,
    }));
    try {
      const isSuccess = await onEditRow?.(row);

      if (isSuccess) {
        setEditingRows((prevRows) => ({
          ...prevRows,
          [row.id]: false,
        }));
        setEditingOriginalValues((prevValues) => {
          const updatedValues = { ...prevValues };
          delete updatedValues[row.id];
          return updatedValues;
        });
      }
    } finally {
      setSavingRows((prevRows) => ({
        ...prevRows,
        [row.id]: false,
      }));
    }
  };

  const handleCancel = (row) => {
    const originalTid = editingOriginalValues[row.id]?.tid;
    if (typeof originalTid !== "undefined") {
      updateProcessorFieldState("tid", originalTid, row);
    }
    setEditingRows((prevRows) => ({
      ...prevRows,
      [row.id]: false,
    }));
    setEditingOriginalValues((prevValues) => {
      const updatedValues = { ...prevValues };
      delete updatedValues[row.id];
      return updatedValues;
    });
  };

  const ActionButtons = ({ row }) => {
    const ALLOWED_PROCESSORS = [
      "ALLINPAY",
      "AMEX_BBMSL",
      "AMEX",
      "GLOBALPAYMENTS",
    ];
    const hasMid = hasProcessorMid(row);
    const isEditMode = Boolean(editingRows[row.id]);
    const isRowSaving = Boolean(savingRows[row.id]);
    const hasEditingRow = Object.values(editingRows).some(Boolean);
    const canEdit =
      ALLOWED_PROCESSORS.includes(row.paymentGateway) &&
      hasMid &&
      !hasEditingRow;

    const buttonSx = {
      width: "80px",
      minWidth: "80px",
    };
    const editButtonSx = {
      padding: 0,
      minWidth: "auto",
      minHeight: "auto",
      textTransform: "none",
      color: row.isPlaceholder ? "#BDBDBD" : c["brandDark12"],
      "&:hover": {
        color: row.isPlaceholder ? "#BDBDBD" : c["brand1"],
        backgroundColor: "transparent",
      },
    };

    if (isEditMode) {
      return (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
          flexWrap="wrap"
        >
          <ButtonMAS
            variant="bordered"
            onClick={(e) => {
              e.stopPropagation();
              handleCancel(row);
            }}
            disabled={disabled || isRowSaving}
            sx={{
              ...buttonSx,
              textTransform: "none",
              color: c["brandDark12"],
              borderColor: c["brandDark12"],
              "&:hover": {
                borderColor: c["brand1"],
                backgroundColor: "transparent",
              },
            }}
          >
            {t(EMPLOYEE_LOCALE_KEY + "employeeDetail.button.cancel")}
          </ButtonMAS>
          <ButtonMAS
            variant="primary"
            isLoading={isRowSaving}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleSave(row);
            }}
            sx={buttonSx}
          >
            {t(EMPLOYEE_LOCALE_KEY + "employeeDetail.button.save")}
          </ButtonMAS>
        </Stack>
      );
    }

    if (canEdit) {
      return (
        <ButtonMAS
          variant="text-only"
          onClick={(e) => {
            e.stopPropagation();
            setEditingRows((prevRows) => ({
              ...prevRows,
              [row.id]: true,
            }));
            setEditingOriginalValues((prevValues) => ({
              ...prevValues,
              [row.id]: { tid: row.tid || "" },
            }));
          }}
          disabled={disabled}
          sx={editButtonSx}
        >
          {t(PAGE_LOCALE_KEY + "employeeDetail.button.edit")}
        </ButtonMAS>
      );
    }
  };

  return (
    <Box sx={styles.container}>
      <Typography variant="h6" sx={styles.title}>
        {t(
          `${PAGE_LOCALE_KEY}employeeDetail.detail.subtitle.processorAccountInfo`,
        )}
      </Typography>
      <Paper elevation={0} sx={styles.paper}>
        <TableContainer>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.tableHead}>
                <TableCell sx={styles.columnWidths.paymentGateway}>
                  Payment Gateway
                </TableCell>
                <TableCell sx={styles.columnWidths.cardType}>
                  Card Type
                </TableCell>
                <TableCell sx={styles.columnWidths.mid}>MID</TableCell>
                <TableCell sx={styles.columnWidths.tid}>TID</TableCell>
                {!isCreate && (
                  <TableCell sx={styles.columnWidths.action}>Action</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody sx={styles.tableBody}>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={isCreate ? 4 : 5} align="center" sx={styles.loadingCell}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : groupedTableRows.length > 0 ? (
                groupedTableRows.flatMap((processorRow) =>
                  processorRow.rows.map((row, rowIndex) => (
                    <TableRow
                      key={`${processorRow.id}-${row.cardType}`}
                      sx={{
                        ...styles.tableRow,
                        ...(rowIndex === 0 ? styles.groupStartRow : {}),
                      }}
                    >
                      {rowIndex === 0 && (
                        <TableCell
                          rowSpan={processorRow.rows.length}
                          sx={{
                            ...styles.columnWidths.paymentGateway,
                            ...styles.groupCell,
                          }}
                        >
                          {processorRow.paymentGateway}
                        </TableCell>
                      )}
                      <TableCell sx={styles.columnWidths.cardType}>
                        {row.cardType}
                      </TableCell>
                      {rowIndex === 0 && (
                        <TableCell
                          rowSpan={processorRow.rows.length}
                          sx={styles.columnWidths.mid}
                        >
                          {isCreate ? (
                            processorRow.midType === "autoGenerate" ? (
                              <div style={styles.autoGenerateCell}>
                                {t(`${PAGE_LOCALE_KEY}createEmployee.autoGenerate`)}
                              </div>
                            ) : (
                              processorRow.mid || "-"
                            )
                          ) : (
                            processorRow.mid || "-"
                          )}
                        </TableCell>
                      )}
                      {rowIndex === 0 && (
                        <TableCell
                          rowSpan={processorRow.rows.length}
                          sx={styles.columnWidths.tid}
                        >
                          {isCreate ? (
                            processorRow.tidType === "autoGenerate" ? (
                              <div style={styles.autoGenerateCell}>
                                {t(`${PAGE_LOCALE_KEY}createEmployee.autoGenerate`)}
                              </div>
                            ) : processorRow.tidType === "outlinedText" &&
                              hasProcessorMid(processorRow) ? (
                              <OutlinedText
                                disabled={disabled}
                                value={processorRow.tid || ""}
                                placeholder={t(
                                  `${PAGE_LOCALE_KEY}createEmployee.tidPlaceholder`,
                                )}
                                onchangeFn={(e) => {
                                  const tid = e.target.value;
                                  updateProcessorFieldState(
                                    "tid",
                                    tid,
                                    processorRow,
                                  );
                                }}
                                style={styles.outlinedText}
                                divStyle={styles.outlinedTextContainer}
                                noErrorMessage={true}
                              />
                            ) : (
                              processorRow.tid || "-"
                            )
                          ) : editingRows[processorRow.id] ? (
                            <OutlinedText
                              disabled={disabled || savingRows[processorRow.id]}
                              value={processorRow?.tid || ""}
                              placeholder={t(
                                `${PAGE_LOCALE_KEY}createEmployee.tidPlaceholder`,
                              )}
                              onchangeFn={(e) => {
                                const tid = e.target.value;
                                updateProcessorFieldState(
                                  "tid",
                                  tid,
                                  processorRow,
                                );
                              }}
                              style={styles.outlinedText}
                              divStyle={styles.outlinedTextContainer}
                              noErrorMessage={true}
                            />
                          ) : (
                            <Box sx={styles.tidCell}>{processorRow.tid || "-"}</Box>
                          )}
                        </TableCell>
                      )}
                      {!isCreate && rowIndex === 0 && (
                        <TableCell
                          rowSpan={processorRow.rows.length}
                          sx={styles.columnWidths.action}
                        >
                          <ActionButtons row={processorRow} />
                        </TableCell>
                      )}
                    </TableRow>
                  )),
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={isCreate ? 4 : 5} align="center" sx={styles.loadingCell}>
                    <Typography color="textSecondary">
                      No processor accounts found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default React.memo(EmployeeProcessorAccInfo);
